import React from 'react'

import {getStatusTheme} from '../../../modes/attendance/attendanceStatusTheme'
import {AttendanceStatusSegment} from '../../../modes/attendance/buildAttendanceVisualSummary'

type AttendanceStatusBarsProps = {
  segments: AttendanceStatusSegment[]
  theme: 'light' | 'dark'
  /** Statuses with genuinely known counts. When supplied, only these get a bar — a status the query
   *  never measured is absent from the list rather than reported as 0. */
  barSegments?: AttendanceStatusSegment[]
}

function countFor(
  segments: AttendanceStatusSegment[],
  key: AttendanceStatusSegment['key']
): number {
  return segments.find((s) => s.key === key)?.count ?? 0
}

/** Falls back to the ring's segments, where a missing status genuinely means zero occurrences
 *  within a fully-measured result set. */
function barsFromSegments(segments: AttendanceStatusSegment[]): AttendanceStatusSegment[] {
  return [
    {
      key: 'present' as const,
      label: 'Present',
      count: countFor(segments, 'present') + countFor(segments, 'excused'),
    },
    {key: 'absent' as const, label: 'Absent', count: countFor(segments, 'absent')},
    {key: 'leave' as const, label: 'Leave', count: countFor(segments, 'leave')},
  ]
}

const AttendanceStatusBars: React.FC<AttendanceStatusBarsProps> = ({
  segments,
  theme,
  barSegments,
}) => {
  // An empty bar carries no information the answer needs: a query filtered to one status draws
  // "Late 0, Absent 0, Leave 0" beside the only row that matters, so the eye reads four statuses
  // where the data measured one. Zeros are dropped and the surviving bars stand alone.
  const resolved = (barSegments?.length ? barSegments : barsFromSegments(segments)).filter(
    (b) => b.count > 0
  )
  if (!resolved.length) return null

  const max = Math.max(...resolved.map((b) => b.count), 1)
  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.08)'
  const muted = theme === 'dark' ? 'rgba(255,255,255,0.65)' : '#6c757d'

  const bars = resolved.map((b) => ({
    label: b.label,
    count: b.count,
    color: getStatusTheme(b.label, theme).color,
  }))

  return (
    <div className='attendance-status-bars d-flex flex-column gap-1' style={{minWidth: 140}}>
      {bars.map((b) => (
        <div key={b.label} className='d-flex align-items-center gap-2'>
          <div style={{width: 44, fontSize: '0.65rem', color: muted, fontWeight: 600}}>
            {b.label}
          </div>
          <div
            className='flex-grow-1 rounded-1 position-relative'
            style={{
              height: 8,
              border: `1px solid ${borderColor}`,
              backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
              overflow: 'hidden',
            }}
            aria-hidden
          >
            <div
              style={{
                width: `${Math.round((b.count / max) * 100)}%`,
                height: '100%',
                backgroundColor: b.color,
              }}
            />
          </div>
          <div style={{width: 16, fontSize: '0.65rem', color: muted, textAlign: 'right'}}>
            {b.count}
          </div>
        </div>
      ))}
    </div>
  )
}

export default AttendanceStatusBars
